import { useState } from "react";
import SectionLabel from "../ui/SectionLabel";
import SplitHeading from "../ui/SplitHeading";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";
import SectionSubtitle from "../ui/SectionSubtitle";

const MODES_STATIC = [
  { num: "01", accent: "#9E9486", accentRgb: "158,148,134" },
  { num: "02", accent: "#C87941", accentRgb: "200,121,65"  },
  { num: "03", accent: "#7CC4D8", accentRgb: "124,196,216" },
  { num: "04", accent: "#A0A8B8", accentRgb: "160,168,184" },
];

export default function Pricing() {
  const { lang } = useLang();
  const [hovered, setHovered] = useState(null);

  const MODES = MODES_STATIC.map((s, i) => ({
    ...s,
    name:     c.pricing.modes[i].name[lang],
    for_whom: c.pricing.modes[i].for_whom[lang],
    effect:   c.pricing.modes[i].effect[lang],
    desc:     c.pricing.modes[i].desc[lang],
    range:    c.pricing.modes[i].range[lang],
  }));

  return (
    <section
      id="wycena"
      className="section-pad relative overflow-hidden"
      style={{ background: "#100F0C" }}
      data-testid="pricing-section"
    >
      <div className="pointer-events-none absolute -top-40 right-0 w-[600px] h-[600px] rounded-full" style={{ background: "radial-gradient(circle, rgba(200,121,65,0.04), rgba(200,121,65,0) 65%)", filter: "blur(80px)" }} />
      <div className="pointer-events-none absolute -bottom-40 -left-20 w-[400px] h-[400px] rounded-full" style={{ background: "radial-gradient(circle, rgba(124,196,216,0.025), rgba(124,196,216,0) 65%)", filter: "blur(80px)" }} />

      <div className="max-w-7xl mx-auto relative">

        {/* ── Header ── */}
        <div className="reveal mb-16 lg:mb-24 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <SectionLabel>{c.pricing.label[lang]}</SectionLabel>
            <SplitHeading
              line1={c.pricing.heading1[lang]}
              line2={<em className="italic text-amber">{c.pricing.ice[lang]}</em>}
              className="font-display font-light leading-[1.04] mt-5 text-white-warm"
              style={{ fontSize: "clamp(2.4rem, 4.8vw, 4rem)" }}
            />
          </div>
          <SectionSubtitle style={{ color: "rgba(242,232,216,0.45)" }}>
            {c.pricing.subtitle[lang]}
          </SectionSubtitle>
        </div>

        {/* ── 2×2 grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {MODES.map((mode, i) => {
            const isHovered = hovered === mode.num;
            return (
              <article
                key={mode.num}
                className={`reveal d${i + 1} flex flex-col`}
                onMouseEnter={() => setHovered(mode.num)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  padding: "3rem 2.75rem",
                  borderRadius: 4,
                  border: isHovered
                    ? `1px solid rgba(${mode.accentRgb},0.28)`
                    : "1px solid rgba(255,255,255,0.07)",
                  background: isHovered ? `rgba(${mode.accentRgb},0.04)` : "rgba(255,255,255,0.02)",
                  transition: "background 400ms ease, border-color 400ms ease",
                  position: "relative",
                }}
              >
                {/* Top accent line on hover */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute", top: 0, left: 0, right: 0, height: 1,
                    background: `rgba(${mode.accentRgb},${isHovered ? 0.6 : 0})`,
                    transition: "background 400ms ease",
                  }}
                />

                {/* Number */}
                <p
                  className="font-mono text-[9px] tracking-[0.34em]"
                  style={{ color: `rgba(${mode.accentRgb},0.45)` }}
                >
                  {mode.num}
                </p>

                {/* Name */}
                <h3
                  className="font-display font-light text-white-warm mt-5"
                  style={{ fontSize: "clamp(1.55rem, 2vw, 1.9rem)", lineHeight: 1.1 }}
                >
                  {mode.name}
                </h3>

                {/* For whom */}
                <p
                  className="font-mono text-[8.5px] tracking-[0.22em] uppercase mt-3 leading-[1.7]"
                  style={{ color: "rgba(242,232,216,0.28)" }}
                >
                  {mode.for_whom}
                </p>

                {/* Hairline */}
                <div
                  className="my-7"
                  style={{
                    height: 1,
                    background: `rgba(${mode.accentRgb},${isHovered ? 0.25 : 0.10})`,
                    transition: "background 400ms ease",
                  }}
                />

                {/* Effect — the heart */}
                <p
                  className="font-display font-light italic"
                  style={{
                    fontSize: "clamp(1.05rem, 1.4vw, 1.18rem)",
                    color: "rgba(242,232,216,0.78)",
                    lineHeight: 1.55,
                  }}
                >
                  &ldquo;{mode.effect}&rdquo;
                </p>

                {/* Description */}
                <p
                  className="font-body text-sm mt-5 leading-[1.85]"
                  style={{ color: "rgba(242,232,216,0.40)" }}
                >
                  {mode.desc}
                </p>

                {/* Bottom — range + CTA */}
                <div className="flex items-end justify-between gap-4 mt-auto pt-10">
                  <div>
                    <p
                      className="font-mono text-[8px] tracking-[0.28em] uppercase mb-1.5"
                      style={{ color: "rgba(242,232,216,0.20)" }}
                    >
                      {lang === "pl" ? "orientacyjnie" : "approx."}
                    </p>
                    <p
                      className="font-display font-light"
                      style={{ fontSize: "clamp(1rem, 1.3vw, 1.12rem)", color: mode.accent }}
                    >
                      {mode.range}
                    </p>
                  </div>

                  <a
                    href="#kontakt"
                    className="font-body text-[10px] tracking-[0.22em] uppercase inline-flex items-center gap-2 group shrink-0"
                    style={{
                      color: mode.accent,
                      opacity: isHovered ? 1 : 0.45,
                      transition: "opacity 300ms",
                    }}
                  >
                    {c.pricing.cta[lang]}
                    <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </a>
                </div>
              </article>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="reveal mt-16 flex flex-col items-center gap-5 text-center">
          <a
            href="#kontakt"
            className="btn-primary"
          >
            {c.pricing.cta_full[lang]}
          </a>
          <p
            className="font-body text-xs max-w-sm leading-[1.75]"
            style={{ color: "rgba(242,232,216,0.28)" }}
          >
            {c.pricing.bottom_note[lang]}
          </p>
        </div>
      </div>
    </section>
  );
}
